import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

/**
 * A small mic button that uses the browser's Web Speech API to dictate into a target field.
 * Gracefully hides itself if the API isn't available (older Firefox, Safari on iOS < 14.5, etc.)
 */
export default function VoiceMic({ onTranscript, testId }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSupported(true);
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    let baseText = "";
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.onresult = (event) => {
      let interim = "", final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) {
        baseText = (baseText ? baseText + " " : "") + final.trim();
        onTranscript(baseText);
      } else if (interim) {
        onTranscript((baseText ? baseText + " " : "") + interim);
      }
    };
    rec._resetBase = (existing) => { baseText = existing || ""; };
    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch (e) { /* noop */ } };
  }, [onTranscript]);

  if (!supported) return null;

  const toggle = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch (e) { /* noop */ }
    } else {
      // Preserve existing content when re-starting
      try {
        rec._resetBase && rec._resetBase(document.activeElement?.value || "");
        rec.start();
      } catch (e) { /* noop */ }
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId || "voice-mic"}
      title={listening ? "Stop dictating" : "Dictate with your voice"}
      className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border transition-all ${listening ? "border-red-400 bg-red-50 text-red-600 animate-pulse" : "border-stone-300 bg-white text-stone-500 hover:border-slate-900 hover:text-slate-950"}`}
    >
      {listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
    </button>
  );
}
